import { useUserRoles } from '@anytag/auth';
import { FormAction } from '@anytag/shared-types';
import { TextField } from '@anytag/shared-molecules';
import { useQueryHelper } from '@anytag/shared-hooks';
import { ProposalJobDescriptionBreakDownMode } from '@anytag/graphql';
import { BaseModal, BaseModalProps, Form, ModalBody, ModalFooter } from '@anytag/shared-atoms';
import { DefaultBudgetInputType, FormValues } from './types';
import { ProposeInfluencersBulkEditSchema } from './schema';

interface BudgetBulkEditModalProps extends Pick<BaseModalProps, 'open' | 'onClose'> {
  currency: string;
  loading: boolean;
  onSuccess?: () => void;
  initialValues?: FormValues;
  jdMode?: ProposalJobDescriptionBreakDownMode;
  onInfluencersBudgetSubmit: (defaultInput: DefaultBudgetInputType) => Promise<void>;
  onSocialAccountBudgetSubmit: (defaultInput: DefaultBudgetInputType) => Promise<void>;
}

export const BudgetBulkEditModal = ({
  open,
  jdMode,
  loading,
  onClose,
  currency,
  onSuccess,
  onSocialAccountBudgetSubmit,
  onInfluencersBudgetSubmit,
  initialValues = { budget: '', influencerCost: '' }
}: BudgetBulkEditModalProps) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { isAdminStaffTalentAgencyPartner } = useUserRoles();

  const currencyElement = currency ? (
    <Form.Field className="w-max flex-none self-end pl-0!">
      <p className="text-sm text-black-60">{currency}</p>
    </Form.Field>
  ) : null;

  const handleBulkFormSubmit: FormAction<FormValues>['onSubmit'] = async ({ budget, influencerCost }) => {
    const budgetNum = budget ? Number(budget) : 0;
    const influencerCostNum = influencerCost ? Number(influencerCost) : 0;
    if (!budget) {
      return;
    }

    const defaultInput: DefaultBudgetInputType = {
      budget: budgetNum,
      influencerCost: influencerCostNum,
      profit: budgetNum - influencerCostNum
    };

    try {
      if (jdMode === ProposalJobDescriptionBreakDownMode.INFLUENCER_BREAKDOWN) {
        await onInfluencersBudgetSubmit(defaultInput);
      } else {
        await onSocialAccountBudgetSubmit(defaultInput);
      }

      enqueueSnackbar(t('succeededUpdateWhitelist'), { variant: 'success' });
      onSuccess?.();

      onClose?.();
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  };

  return (
    <BaseModal open={open} onClose={onClose} title={t('Dialog.Bulk Edit')} asChild>
      <Form.Provider<FormValues>
        defaultValues={initialValues}
        zodSchema={ProposeInfluencersBulkEditSchema}
        onSubmit={handleBulkFormSubmit}
      >
        {({ values, formState }) => (
          <>
            <ModalBody>
              <Form.Fields>
                <TextField<keyof FormValues> type="number" name="budget" placeholder="0" title={t('TextForm.Budget')} />
                {currencyElement}

                {isAdminStaffTalentAgencyPartner ? (
                  <>
                    <TextField<keyof FormValues>
                      type="number"
                      placeholder="0"
                      name="influencerCost"
                      title={t('TextForm.Influencer Cost')}
                    />
                    {currencyElement}
                  </>
                ) : null}
              </Form.Fields>
            </ModalBody>

            <ModalFooter
              cancelButton={{ onClick: onClose }}
              submitButton={{
                type: 'submit',
                disabled: !values.budget,
                title: t('Button.Apply'),
                loading: formState.isSubmitting || loading
              }}
            />
          </>
        )}
      </Form.Provider>
    </BaseModal>
  );
};
